import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Eye, Save } from "lucide-react";

export default function Settings() {
  const [s, setS] = useState(null);
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const r = await api.get("/settings");
    setS(r.data);
  };
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const {
        bot_username,
        bot_token_configured,
        ...payload
      } = s;
      await api.put("/settings", payload);
      toast.success("Ajustes guardados");
    } catch (e) {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  if (!s) return <div className="text-zinc-500">Cargando…</div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <div>
        <div className="overline mb-2">Operación del bot</div>
        <h1 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl tracking-tight">Ajustes del bot</h1>
        <p className="text-sm text-zinc-500 mt-2">
          Configura el mensaje de bienvenida, instrucciones al modelo IA, datos bancarios y costos.
        </p>
      </div>

      <div className="bg-zinc-900/70 border border-zinc-800 rounded-sm p-5 space-y-2">
        <div className="overline">Estado del bot</div>
        <div className="flex items-center gap-3">
          <div className={`w-2.5 h-2.5 rounded-full ${s.bot_username ? "bg-emerald-500" : "bg-red-500"}`} />
          <div className="font-mono text-sm" data-testid="bot-status-indicator">
            {s.bot_username ? `@${s.bot_username} está en línea` : "Bot desconectado"}
          </div>
        </div>
        <div className="text-xs text-zinc-500">
          Token: {s.bot_token_configured ? "configurado" : "no configurado"}
        </div>
      </div>

      {/* Welcome */}
      <div className="bg-zinc-900/70 border border-zinc-800 rounded-sm p-5 space-y-3">
        <Label className="overline">Mensaje de bienvenida (/start)</Label>
        <Textarea
          value={s.welcome_message}
          onChange={(e) => setS({ ...s, welcome_message: e.target.value })}
          rows={6}
          className="bg-zinc-950 border-zinc-800 rounded-sm font-mono text-sm"
          data-testid="welcome-textarea"
        />
      </div>

      {/* System prompt */}
      <div className="bg-zinc-900/70 border border-zinc-800 rounded-sm p-5 space-y-3">
        <Label className="overline">System prompt (personalidad del bot — Claude Sonnet 4.5)</Label>
        <Textarea
          value={s.bot_system_prompt}
          onChange={(e) => setS({ ...s, bot_system_prompt: e.target.value })}
          rows={6}
          className="bg-zinc-950 border-zinc-800 rounded-sm font-mono text-sm"
          data-testid="system-prompt-textarea"
        />
      </div>

      {/* Tienda en mantenimiento toggle */}
      <div
        className={
          "border rounded-sm p-5 " +
          (s.purchases_disabled
            ? "bg-amber-500/5 border-amber-500/40"
            : "bg-zinc-900/70 border-zinc-800")
        }
        data-testid="purchases-disabled-card"
      >
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id="purchases_disabled"
            checked={!!s.purchases_disabled}
            onChange={(e) => setS({ ...s, purchases_disabled: e.target.checked })}
            data-testid="purchases-disabled-toggle"
            className="mt-1 w-5 h-5 accent-amber-500 cursor-pointer"
          />
          <label htmlFor="purchases_disabled" className="cursor-pointer flex-1">
            <div className="font-bold text-sm flex items-center gap-2">
              🔧 Tienda en mantenimiento (botones de compra desactivados)
              {s.purchases_disabled && (
                <span className="text-[10px] uppercase tracking-wider bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded font-mono">
                  ACTIVO
                </span>
              )}
            </div>
            <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
              Cuando está activado, todos los flujos de compra (Stripe, transferencias bancarias,
              botones del bot) muestran "Tienda en mantenimiento — vuelve pronto". Los comandos,
              chat, OSINT y saldos existentes siguen funcionando con normalidad.
              Útil para ajustar precios sin que usuarios compren a precios viejos.
            </p>
          </label>
        </div>
      </div>

      {/* Bank info */}
      <div className="bg-zinc-900/70 border border-zinc-800 rounded-sm p-5 space-y-3">
        <div className="flex items-center justify-between">
          <Label className="overline">Datos bancarios (se muestran al pedir /comprar)</Label>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPreview(!preview)}
            data-testid="toggle-preview-button"
            className="text-[#2AABEE]"
          >
            <Eye size={14} className="mr-1" /> {preview ? "Editar" : "Vista previa"}
          </Button>
        </div>
        {!preview ? (
          <Textarea
            value={s.bank_info_text}
            onChange={(e) => setS({ ...s, bank_info_text: e.target.value })}
            rows={10}
            className="bg-zinc-950 border-zinc-800 rounded-sm font-mono text-sm"
            data-testid="bank-info-textarea"
          />
        ) : (
          <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4 font-mono text-sm whitespace-pre-wrap text-zinc-300" data-testid="bank-info-preview">
            {s.bank_info_text}
          </div>
        )}
        <div className="text-[11px] text-zinc-600">
          Soporta Markdown de Telegram: *negrita*, _itálica_, `código`
        </div>
      </div>

      {/* Admin Telegram IDs for push notifications */}
      <div className="bg-zinc-900/70 border border-zinc-800 rounded-sm p-5 space-y-3">
        <Label className="overline">Notificaciones al admin en Telegram</Label>
        <p className="text-xs text-zinc-500">
          Lista de *IDs numéricos* de Telegram separados por coma. Todos reciben alerta con
          botones "✅ Aprobar" / "❌ Rechazar" cuando un usuario envía comprobante. Para obtener tu
          ID escribe a <span className="font-mono text-[#2AABEE]">@userinfobot</span> en Telegram.
        </p>
        <div className="flex items-center gap-3">
          <Input
            type="text"
            placeholder="Ej: 6075075436, 123456789"
            value={(s.admin_telegram_ids || []).join(", ")}
            onChange={(e) => setS({
              ...s,
              admin_telegram_ids: e.target.value
                .split(",")
                .map((x) => parseInt(x.trim(), 10))
                .filter((n) => !isNaN(n) && n > 0),
            })}
            className="bg-zinc-950 border-zinc-800 rounded-sm max-w-[420px] font-mono"
            data-testid="admin-telegram-ids-input"
          />
          <span className="text-xs text-zinc-500">
            {(s.admin_telegram_ids || []).length > 0
              ? `✅ ${(s.admin_telegram_ids || []).length} admin(s) configurado(s)`
              : "⚠️ Sin configurar"}
          </span>
        </div>
      </div>

      {/* Trial promo window */}
      <div className="bg-zinc-900/70 border border-zinc-800 rounded-sm p-5 space-y-3">
        <Label className="overline">Ventana promocional del trial</Label>
        <p className="text-xs text-zinc-500">
          Durante la promoción, cada usuario nuevo recibe el trial de 1 hora automáticamente.
          Cuando expira, el trial se desactiva solo (nuevos usuarios ya no lo reciben).
          Puedes extender o acortar cambiando la fecha.
        </p>
        <div className="flex items-center gap-3">
          <Input
            type="datetime-local"
            value={
              s.trial_promo_ends_at
                ? s.trial_promo_ends_at.slice(0, 16)
                : ""
            }
            onChange={(e) => {
              const v = e.target.value;
              setS({
                ...s,
                trial_promo_ends_at: v ? new Date(v).toISOString() : null,
              });
            }}
            className="bg-zinc-950 border-zinc-800 rounded-sm max-w-[260px] font-mono"
            data-testid="trial-promo-input"
          />
          <span className="text-xs text-zinc-500">
            {s.trial_promo_ends_at
              ? new Date(s.trial_promo_ends_at) > new Date()
                ? `✅ Promo activa — termina en ${Math.ceil(
                    (new Date(s.trial_promo_ends_at) - new Date()) / 86400000
                  )} días`
                : "⚠️ Promo expirada — nuevos usuarios no reciben trial"
              : "Sin configurar (trial siempre activo)"}
          </span>
        </div>
      </div>

      {/* Costs */}
      <div className="bg-zinc-900/70 border border-zinc-800 rounded-sm p-5 space-y-3">
        <Label className="overline">Costos por acción (créditos)</Label>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-xs text-zinc-500">Mensaje de texto</Label>
            <Input
              type="number"
              min="0"
              value={s.credit_cost_text}
              onChange={(e) => setS({ ...s, credit_cost_text: Number(e.target.value) })}
              className="bg-zinc-950 border-zinc-800 rounded-sm"
              data-testid="cost-text-input"
            />
          </div>
          <div>
            <Label className="text-xs text-zinc-500">Generar imagen</Label>
            <Input
              type="number"
              min="0"
              value={s.credit_cost_image}
              onChange={(e) => setS({ ...s, credit_cost_image: Number(e.target.value) })}
              className="bg-zinc-950 border-zinc-800 rounded-sm"
              data-testid="cost-image-input"
            />
          </div>
          <div>
            <Label className="text-xs text-zinc-500">Nota de voz</Label>
            <Input
              type="number"
              min="0"
              value={s.credit_cost_voice}
              onChange={(e) => setS({ ...s, credit_cost_voice: Number(e.target.value) })}
              className="bg-zinc-950 border-zinc-800 rounded-sm"
              data-testid="cost-voice-input"
            />
          </div>
        </div>
      </div>

      {/* Security feature costs */}
      <div className="bg-zinc-900/70 border border-zinc-800 rounded-sm p-5 space-y-3">
        <Label className="overline">Costos de seguridad y privacidad (créditos)</Label>
        <p className="text-xs text-zinc-500">
          Premium y usuarios en trial NO pagan estos costos (es parte del valor incluido).
          El escaneo automático de URLs en mensajes siempre es gratis.
        </p>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-xs text-zinc-500">/escanear por URL</Label>
            <Input
              type="number"
              min="0"
              value={s.credit_cost_url_scan ?? 3}
              onChange={(e) => setS({ ...s, credit_cost_url_scan: Number(e.target.value) })}
              className="bg-zinc-950 border-zinc-800 rounded-sm"
              data-testid="cost-url-scan-input"
            />
          </div>
          <div>
            <Label className="text-xs text-zinc-500">/privacidad por guía</Label>
            <Input
              type="number"
              min="0"
              value={s.credit_cost_privacy_guide ?? 3}
              onChange={(e) => setS({ ...s, credit_cost_privacy_guide: Number(e.target.value) })}
              className="bg-zinc-950 border-zinc-800 rounded-sm"
              data-testid="cost-privacy-guide-input"
            />
          </div>
          <div>
            <Label className="text-xs text-zinc-500">/privado por 1 hora</Label>
            <Input
              type="number"
              min="0"
              value={s.credit_cost_private_hour ?? 15}
              onChange={(e) => setS({ ...s, credit_cost_private_hour: Number(e.target.value) })}
              className="bg-zinc-950 border-zinc-800 rounded-sm"
              data-testid="cost-private-hour-input"
            />
          </div>
        </div>
      </div>

      {/* Free trial */}
      <div className="bg-zinc-900/70 border border-zinc-800 rounded-sm p-5 space-y-3">
        <Label className="overline">Trial gratis para usuarios nuevos</Label>
        <p className="text-xs text-zinc-500">
          Cada usuario nuevo recibe estos minutos de acceso ilimitado al empezar /start.
          Pon 0 para desactivar el trial.
        </p>
        <div className="flex items-center gap-3">
          <Input
            type="number"
            min="0"
            value={s.free_trial_minutes ?? 0}
            onChange={(e) => setS({ ...s, free_trial_minutes: Number(e.target.value) })}
            className="bg-zinc-950 border-zinc-800 rounded-sm max-w-[140px]"
            data-testid="free-trial-input"
          />
          <span className="text-sm text-zinc-400">minutos</span>
        </div>
      </div>

      {/* Referral bonus */}
      <div className="bg-zinc-900/70 border border-zinc-800 rounded-sm p-5 space-y-3">
        <Label className="overline">Programa de referidos</Label>
        <p className="text-xs text-zinc-500">
          Créditos que recibe el usuario referidor cuando su invitado hace su primera compra
          aprobada. El invitado siempre recibe +10 créditos extra al entrar con un link de
          referido. Pon 0 para desactivar el bonus al referidor.
        </p>
        <div className="flex items-center gap-3">
          <Input
            type="number"
            min="0"
            value={s.referral_bonus_credits ?? 0}
            onChange={(e) => setS({ ...s, referral_bonus_credits: Number(e.target.value) })}
            className="bg-zinc-950 border-zinc-800 rounded-sm max-w-[140px]"
            data-testid="referral-bonus-input"
          />
          <span className="text-sm text-zinc-400">créditos al referidor</span>
        </div>
      </div>

      {/* API Keys (third-party integrations) */}
      <div className="bg-zinc-900/70 border border-zinc-800 rounded-sm p-5 space-y-3">
        <Label className="overline">API Keys de integraciones externas</Label>
        <p className="text-xs text-zinc-500">
          Claves de servicios de terceros usados por las herramientas OSINT. Obtén tu Shodan key
          gratis en <span className="font-mono text-[#2AABEE]">account.shodan.io</span>.
        </p>
        <div>
          <Label className="text-xs text-zinc-500">Shodan API Key (para /shodan)</Label>
          <Input
            type="text"
            placeholder="Ej: LuyVjbcc8u3VYdinHScwoTy0Fd5aZTON"
            value={s.shodan_api_key || ""}
            onChange={(e) => setS({ ...s, shodan_api_key: e.target.value })}
            className="bg-zinc-950 border-zinc-800 rounded-sm font-mono text-sm"
            data-testid="shodan-api-key-input"
          />
          <div className="text-[11px] text-zinc-600 mt-1">
            {s.shodan_api_key
              ? `✅ Configurada (${s.shodan_api_key.length} chars)`
              : "⚠️ Sin configurar — /shodan no funcionará"}
          </div>
        </div>

        <div>
          <Label className="text-xs text-zinc-500">GitHub Personal Access Token (para /github code search)</Label>
          <Input
            type="password"
            placeholder="Ej: github_pat_11CBNC..."
            value={s.github_token || ""}
            onChange={(e) => setS({ ...s, github_token: e.target.value })}
            className="bg-zinc-950 border-zinc-800 rounded-sm font-mono text-sm"
            data-testid="github-token-input"
          />
          <div className="text-[11px] text-zinc-600 mt-1">
            {s.github_token
              ? `✅ Configurado (${s.github_token.length} chars) — 5000 req/h`
              : "⚠️ Sin configurar — búsqueda de código GitHub no funcionará"}
          </div>
        </div>
      </div>

      {/* Misc */}
      <div className="bg-zinc-900/70 border border-zinc-800 rounded-sm p-5 space-y-4">
        <div>
          <Label className="overline">Moneda mostrada en Telegram</Label>
          <Select value={s.currency_display} onValueChange={(v) => setS({ ...s, currency_display: v })}>
            <SelectTrigger className="bg-zinc-950 border-zinc-800 rounded-sm max-w-xs mt-2" data-testid="currency-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800 rounded-sm">
              <SelectItem value="USD">Solo USD</SelectItem>
              <SelectItem value="MXN">Solo MXN</SelectItem>
              <SelectItem value="BOTH">USD + MXN</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-3 pt-2 border-t border-zinc-800">
          <Switch
            checked={s.maintenance_mode}
            onCheckedChange={(v) => setS({ ...s, maintenance_mode: v })}
            data-testid="maintenance-switch"
          />
          <div>
            <Label className="text-sm">Modo mantenimiento</Label>
            <div className="text-xs text-zinc-500">El bot responderá con un aviso y no procesará mensajes.</div>
          </div>
        </div>
      </div>

      <div className="sticky bottom-4 flex justify-end">
        <Button
          onClick={save}
          disabled={saving}
          data-testid="save-settings-button"
          className="bg-[#2AABEE] hover:bg-[#1C93D4] rounded-sm shadow-lg"
        >
          <Save size={14} className="mr-2" /> Guardar ajustes
        </Button>
      </div>
    </div>
  );
}
